import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Upload from "@/pages/Upload";
import Rates from "@/pages/Rates";
import Franchises from "@/pages/Franchises";
import Distributors from "@/pages/Distributors";
import Photos from "@/pages/Photos";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import DiamondShapes from "@/pages/DiamondShapes";
import Trash from "@/pages/Trash";
import FranchiseView from "@/pages/FranchiseView";
import PublicCatalogueView from "@/pages/PublicCatalogueView";
import UploadHistory from "@/pages/UploadHistory";
import FranchisePortal from "@/pages/FranchisePortal";
import DistributionRates from "@/pages/DistributionRates";
import DistributionLinks from "@/pages/DistributionLinks";
import DistributionCatalog from "@/pages/DistributionCatalog";
import DistributionCollection from "@/pages/DistributionCollection";
import CatalogUpload from "@/pages/CatalogUpload";
import DistributorUpload from "@/pages/DistributorUpload";
import UploadDesigns from "@/pages/UploadDesigns";
import Designs from "@/pages/Designs";
import MasterCatalogHome from "@/pages/MasterCatalogHome";
import MasterCatalogBrowse from "@/pages/MasterCatalogBrowse";
import MasterCatalogLinkView from "@/pages/MasterCatalogLinkView";
import MasterCatalogPartner from "@/pages/MasterCatalogPartner";
import Wholesalers from "@/pages/Wholesalers";
import WholesalerDetail from "@/pages/WholesalerDetail";
import WholesalerPortal from "@/pages/WholesalerPortal";
import RetailerShopfront from "@/pages/RetailerShopfront";
import AdminLayout from "@/components/AdminLayout";

function Protected({ allowedRoles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FAF8F3]">
        <div className="font-serif text-2xl text-[#0D1B2A] tracking-widest">APS</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={["master_admin", "ho_staff"].includes(user.role) ? "/" : "/portal"} replace />;
  }
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (["master_admin", "ho_staff"].includes(user.role)) return null; // child route renders
  return <Navigate to="/portal" replace />;
}

function App() {
  useEffect(() => {
    // CS 22.11 -- wholesaler-created retailer links (/wr/) own their unbranded title.
    if (!window.location.pathname.startsWith("/wr/")) document.title = "APS — Ashth Pricing System";
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/franchise/:token" element={<FranchiseView />} />
            <Route path="/franchise/:token/product/:jewelCode" element={<FranchiseView />} />
            <Route path="/distributor/:token" element={<PublicCatalogueView kind="distributor" />} />
            <Route path="/retailer/:token" element={<PublicCatalogueView kind="retailer" />} />

            {/* Distribution Master Catalog — public selection links (separate from /distributor and /retailer above) */}
            <Route path="/dist/distributor/:token" element={<DistributionCollection kind="distributor" />} />
            <Route path="/dist/retailer/:token" element={<DistributionCollection kind="retailer" />} />

            {/* CS 21 — Wholesaler public portal (PIN-gated) */}
            <Route path="/w/:token" element={<WholesalerPortal />} />

            {/* CS 22.0 -- Franchise Master Catalog partner selection link (PIN-gated) */}
            <Route path="/mc/:token" element={<MasterCatalogPartner />} />

            {/* CS 21.38 — Wholesaler-created Retailer Link shopfront (password-gated) */}
            <Route path="/wr/:token" element={<RetailerShopfront />} />

            {/* Franchise users portal */}
            <Route
              path="/portal/*"
              element={
                <Protected allowedRoles={["franchise_owner", "franchise_team"]}>
                  <FranchisePortal />
                </Protected>
              }
            />

            {/* Master admin layout */}
            <Route
              path="/"
              element={
                <Protected allowedRoles={["master_admin", "ho_staff"]}>
                  <AdminLayout />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="products" element={<Products />} />
              <Route path="upload" element={<Upload />} />
              <Route path="upload-designs" element={<UploadDesigns />} />
              <Route path="designs" element={<Designs />} />
              <Route path="master-catalog" element={<MasterCatalogHome />} />
              <Route path="master-catalog/browse" element={<MasterCatalogBrowse />} />
              <Route path="master-catalog/links/:id" element={<MasterCatalogLinkView />} />
              <Route path="upload-history" element={<UploadHistory />} />
              <Route path="rates" element={<Rates />} />
              <Route path="shapes" element={<DiamondShapes />} />
              <Route path="franchises" element={<Franchises />} />
              <Route path="distributors" element={<Distributors />} />
              <Route path="distributor-upload" element={<DistributorUpload />} />
              <Route path="photos" element={<Photos />} />
              <Route path="reports" element={<Reports />} />
              <Route path="users" element={<Users />} />
              <Route path="trash" element={<Trash />} />
              <Route path="distribution/catalog" element={<DistributionCatalog />} />
              <Route path="distribution/catalog-upload" element={<CatalogUpload />} />
              <Route path="distribution/rates" element={<DistributionRates />} />
              <Route path="distribution/links" element={<DistributionLinks />} />
              <Route path="wholesalers" element={<Wholesalers />} />
              <Route path="wholesalers/:id" element={<WholesalerDetail />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
